import { c as _c } from "react/compiler-runtime";
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectAutoFocusSearch, setAutoFocusSearch } from '@repo/store/maps/slices/clientContext';
import { selectCurrentMapInfo, selectFilterState, setSearchTerm } from '@repo/store/maps/slices/mapData';
import { CUSTOM_PINS_CATEGORY_ID } from '@repo/store/maps/utils/mapData';
import { HostType } from '@repo/types/host';
import { Fragment, useEffect, useRef, useState } from 'react';
import { ChecklistEntryButton } from '../checklist/checklist-entry-button';
import { MapsAuthModal } from '../components/maps-auth-modal';
import { MapDetailsModal } from '../map-details-modal/map-details-modal';
import { MapDiscoveryProgress } from '../map-discovery-progress';
import { HeatmapEventsSection } from '../map-sidebar/heatmap-events-section';
import { MapCategorySection } from '../map-sidebar/map-category-section';
import { MapFilters } from '../map-sidebar/map-filters';
import { MapRegionFilters } from '../map-sidebar/map-region-filters';
import { MapSearch } from '../map-sidebar/map-search';
import { MapSearchResults } from '../map-sidebar/map-search-results';
import { MapSelector } from '../map-sidebar/map-selector';
import { ResetFoundPins } from '../map-sidebar/reset-found-pins';
import { SessionHistorySection } from '../map-sidebar/session-history-section';
import { MobileLanguageSelector } from './mobile-language-selector';
import { MobileLayerControlButton } from './mobile-layer-control-button';
interface MobileSidebarContentProps {
  onSearchFocusChange?: (focused: boolean) => void;
  onSessionHistoryClick?: () => void;
  onChecklistClick?: () => void;
}
const SEARCH_DEBOUNCE_MS = 400;
export const MobileSidebarContent = t0 => {
  const $ = _c(66);
  const {
    onSearchFocusChange,
    onSessionHistoryClick,
    onChecklistClick
  } = t0;
  const dispatch = useAppDispatch();
  const hostType = useHostType();
  const isDesktop = hostType === HostType.APP || hostType === HostType.OVERLAY;
  const isLoggedIn = useAppSelector(_temp);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isMapDetailsModalOpen, setIsMapDetailsModalOpen] = useState(false);
  const {
    groupedCategories
  } = useAppSelector(_temp2);
  const filters = useAppSelector(selectFilterState);
  const currentMapInfo = useAppSelector(selectCurrentMapInfo);
  const isChecklistPublished = currentMapInfo?.isChecklistPublished ?? false;
  const [inputValue, setInputValue] = useState(filters.searchTerm);
  const debounceRef = useRef(null);
  let t1;
  if ($[0] !== dispatch) {
    t1 = e => {
      const newValue = e.target.value;
      setInputValue(newValue);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        dispatch(setSearchTerm(newValue));
      }, SEARCH_DEBOUNCE_MS);
    };
    $[0] = dispatch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSearchChange = t1;
  let t2;
  if ($[2] !== isLoggedIn) {
    t2 = () => {
      if (!isLoggedIn) {
        setIsLoginModalOpen(true);
      } else {
        setIsMapDetailsModalOpen(true);
      }
    };
    $[2] = isLoggedIn;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleMapDiscoveryProgressClick = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = css({
      flex: "[1]",
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
      overflowX: "hidden",
      overscrollBehavior: "none",
      width: "full",
      gap: "8",
      WebkitOverflowScrolling: "touch",
      contain: "[layout style]"
    });
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const scrollableStyles = t3;
  let t4;
  let t5;
  if ($[5] !== filters.searchTerm) {
    t4 = () => {
      setInputValue(filters.searchTerm);
    };
    t5 = [filters.searchTerm];
    $[5] = filters.searchTerm;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    t7 = [];
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  const autoFocusSearch = useAppSelector(selectAutoFocusSearch);
  let t8;
  let t9;
  if ($[10] !== autoFocusSearch || $[11] !== dispatch || $[12] !== onSearchFocusChange) {
    t8 = () => {
      if (autoFocusSearch) {
        onSearchFocusChange?.(true);
        dispatch(setAutoFocusSearch(false));
      }
    };
    t9 = [autoFocusSearch, onSearchFocusChange, dispatch];
    $[10] = autoFocusSearch;
    $[11] = dispatch;
    $[12] = onSearchFocusChange;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = css.raw({
      paddingX: "16",
      paddingTop: "20"
    });
    t11 = css({
      display: "flex",
      alignItems: "center",
      gap: "8",
      width: "full",
      minHeight: "[44px]",
      flexShrink: "[0]"
    });
    t12 = css({
      flex: "[1]",
      minWidth: "[0]",
      width: "full"
    });
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
  }
  let t13;
  let t14;
  if ($[18] !== onSearchFocusChange) {
    t13 = () => onSearchFocusChange?.(true);
    t14 = () => onSearchFocusChange?.(false);
    $[18] = onSearchFocusChange;
    $[19] = t13;
    $[20] = t14;
  } else {
    t13 = $[19];
    t14 = $[20];
  }
  let t15;
  if ($[21] !== handleSearchChange || $[22] !== inputValue || $[23] !== t13 || $[24] !== t14) {
    t15 = <div className={t12}><MapSearch value={inputValue} onChange={handleSearchChange} onFocus={t13} onBlur={t14} /></div>;
    $[21] = handleSearchChange;
    $[22] = inputValue;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <MobileLayerControlButton />;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== isDesktop) {
    t17 = !isDesktop && <MobileLanguageSelector />;
    $[27] = isDesktop;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t15 || $[30] !== t17) {
    t18 = <div className={t11}>{t15}{t16}{t17}</div>;
    $[29] = t15;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <MapSelector />;
    t20 = css({
      flexShrink: "[0]",
      width: "full"
    });
    $[32] = t19;
    $[33] = t20;
  } else {
    t19 = $[32];
    t20 = $[33];
  }
  let t21;
  if ($[34] !== handleMapDiscoveryProgressClick) {
    t21 = <div className={t20}><MapDiscoveryProgress onClick={handleMapDiscoveryProgressClick} /></div>;
    $[34] = handleMapDiscoveryProgressClick;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <MapFilters isMobile={true} />;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== onSessionHistoryClick) {
    t23 = onSessionHistoryClick && <SessionHistorySection onClick={onSessionHistoryClick} />;
    $[37] = onSessionHistoryClick;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== isChecklistPublished || $[40] !== onChecklistClick) {
    t24 = isChecklistPublished && onChecklistClick && <ChecklistEntryButton onClick={onChecklistClick} />;
    $[39] = isChecklistPublished;
    $[40] = onChecklistClick;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className={css({
      borderBottom: "[1px solid]",
      borderColor: "whiteAlpha.5"
    })} />;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== filters.searchTerm || $[44] !== groupedCategories || $[45] !== inputValue) {
    t26 = inputValue.trim() || filters.searchTerm.trim() ? <MapSearchResults /> : <FlexColumn gap="12" width="full"><MapRegionFilters isMobile={true} />{groupedCategories?.map(category => <Fragment key={category.externalId}>{category.externalId === CUSTOM_PINS_CATEGORY_ID && <HeatmapEventsSection searchTerm={inputValue || filters.searchTerm} />}<MapCategorySection category={category} searchTerm={inputValue || filters.searchTerm} /></Fragment>)}</FlexColumn>;
    $[43] = filters.searchTerm;
    $[44] = groupedCategories;
    $[45] = inputValue;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <FlexColumn justify="center" align="center" className={css.raw({
      paddingY: "12"
    })}><ResetFoundPins /></FlexColumn>;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t23 || $[49] !== t24 || $[50] !== t26) {
    t28 = <div className={scrollableStyles}>{t22}{t23}{t24}{t25}{t26}{t27}</div>;
    $[48] = t23;
    $[49] = t24;
    $[50] = t26;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== t18 || $[53] !== t21 || $[54] !== t28) {
    t29 = <FlexColumn gap="8" width="full" height="full" className={t10}>{t18}{t19}{t21}{t28}</FlexColumn>;
    $[52] = t18;
    $[53] = t21;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = details => setIsLoginModalOpen(details.open);
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== isLoginModalOpen) {
    t31 = <MapsAuthModal isOpen={isLoginModalOpen} onOpenChange={t30} trigger="discovery-progress" size="sm" />;
    $[57] = isLoginModalOpen;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = details_0 => setIsMapDetailsModalOpen(details_0.open);
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== isMapDetailsModalOpen) {
    t33 = <MapDetailsModal isOpen={isMapDetailsModalOpen} onOpenChange={t32} />;
    $[60] = isMapDetailsModalOpen;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] !== t29 || $[63] !== t31 || $[64] !== t33) {
    t34 = <>{t29}{t31}{t33}</>;
    $[62] = t29;
    $[63] = t31;
    $[64] = t33;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  return t34;
};
function _temp(state) {
  return state.auth.isAuthenticated;
}
function _temp2(state_0) {
  return state_0.mapData;
}